import api from "app/api";
import useExcelUrl from "app/useExcelUrl";
import { globalMessageHandler } from "common";
import useSettings from "features/settings/useSettings";
import moment from "moment";
import { createContext, useContext, useEffect, useState } from "react";

export type PivotReportData = {
  raw_data: Array<{
    time: string;
    date: string;
    day_name: string;
    hour: number;
    program_name: string;
    program_code: string;
    program_color: string;
    program_display: string;
    boat_name: string;
    boat_color: string;
    utilization: string;
    booked_guests: number;
    booking_count: number;
  }>;
  pivot_matrix: {
    matrix: {
      [rowKey: string]: {
        [columnKey: string]: {
          value: number;
          secondary_value?: number;
          is_valid: boolean;
          type: "data" | "empty";
        };
      };
    };
    row_labels: string[];
    column_labels: string[];
    meta?: {
      total_combinations?: number;
      valid_combinations?: number;
      empty_combinations?: number;
    };
    heat_map_config: {
      type?: "single_color" | "continuous" | "threshold";
      min_value?: number;
      max_value?: number;
      base_color?: string;
      export_colors?: {
        light_theme: string;
        dark_theme: string;
      };
      // Legacy support
      gradient_type?: "opacity" | "gradient" | "hue";
      color_stops?: Array<{
        position: number;
        color: string;
      }>;
      min_color?: string;
      max_color?: string;
      thresholds?: Array<{
        min: number;
        max: number;
        color: string;
        label: string;
      }>;
    };
    secondary_heat_map_config?: {
      type?: "single_color" | "continuous" | "threshold";
      min_value?: number;
      max_value?: number;
      base_color?: string;
      export_colors?: {
        light_theme: string;
        dark_theme: string;
      };
      // Legacy support
      gradient_type?: "opacity" | "gradient" | "hue";
      color_stops?: Array<{
        position: number;
        color: string;
      }>;
      min_color?: string;
      max_color?: string;
      thresholds?: Array<{
        min: number;
        max: number;
        color: string;
        label: string;
      }>;
    };
  };
  config: {
    report_type: string;
    row_dimension: string;
    column_dimension: string;
    primary_measure: string;
    secondary_measure?: string;
    available_dimensions: {
      [key: string]: string;
    };
    available_measures: {
      [key: string]: {
        label: string;
        type: "avg" | "sum";
        format: "percentage" | "number" | "currency";
        colors?: {
          base: string;
          light: string;
          dark: string;
        };
      };
    };
    available_filters: string[];
    defaults: {
      row_dimension: string;
      column_dimension: string;
      primary_measure: string;
      secondary_measure?: string;
    };
  };
};

export type PivotReportFilterType = {
  reportType: "event" | "boat" | "booking";
  from: string; //date string like "2024-01-01"
  to: string; //date string like "2024-01-01"
  rowDimension?: string;
  columnDimension?: string;
  primaryMeasure?: string;
  secondaryMeasure?: string;
  boatIds?: number[];
  programIds?: number[];
  partnerIds?: number[];
  partnerGroupIds?: number[];
};

export type PivotReportDataContextType = {
  data: PivotReportData | null;
  dataReportType: string | null;
  filter: PivotReportFilterType;
  setFilter: (filter: PivotReportFilterType) => void;
  load: () => Promise<void>;
  loading: boolean;
  excelUrl?: string;
};

export const PivotReportDataContext =
  createContext<PivotReportDataContextType | null>(null);

export default function usePivotReportData() {
  const [settings] = useSettings({ key: "pivotReport" });

  const [filter, setFilter] = useState<PivotReportFilterType>(() => {
    const eventParams = settings?.event;
    return {
      reportType: "event",
      from: moment().startOf("week").format("YYYY-MM-DD"),
      to: moment().endOf("week").format("YYYY-MM-DD"),
      rowDimension: eventParams?.rowDimension,
      columnDimension: eventParams?.columnDimension,
      primaryMeasure: eventParams?.primaryMeasure,
      secondaryMeasure: eventParams?.secondaryMeasure,
    };
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PivotReportData | null>(null);
  const [dataReportType, setDataReportType] = useState<string | null>(null);

  async function load() {
    //api is an axios instance
    setLoading(true);
    const loadedReportType = filter.reportType;
    const {
      success,
      data: responseData,
      error,
    } = await api.get(`reports/pivot/${filter.reportType}`, {
      params: {
        ...filter,
        currency: settings?.currency,
      },
    });
    setLoading(false);
    if (success) {
      setData(responseData);
      setDataReportType(loadedReportType);
    } else {
      globalMessageHandler.alert({
        severity: "error",
        message: typeof error === "string" ? error : JSON.stringify(error),
      });
    }
  }

  const excelUrl = useExcelUrl(
    "reports/pivot",
    null,
    {
      ...filter,
      currency: settings?.currency,
    },
    false,
  );

  useEffect(() => {
    load();
  }, [
    filter.reportType,
    filter.from,
    filter.to,
    filter.rowDimension,
    filter.columnDimension,
    filter.primaryMeasure,
    filter.secondaryMeasure,
    filter.programIds,
    filter.boatIds,
    filter.partnerIds,
    filter.partnerGroupIds,
    settings?.currency,
  ]);

  return {
    data,
    dataReportType,
    filter,
    setFilter,
    load,
    loading,
    excelUrl,
  };
}

export function usePivotReportDataContext() {
  const context = useContext(PivotReportDataContext);
  if (!context) {
    throw Error(
      "usePivotReportDataContext cannot be used outside of PivotReportDataProvider.",
    );
  }
  return context;
}
